/**
 * Every Datadog feature flag the client UI reads.
 *
 * This mirrors the `RingbaXFlags` registry that ringba-x's `backend-feature-flag-service` change
 * plans (`pkgs/RingbaX.FeatureFlags`): exactly the entries whose `Surface` is `Ui` or `Both`,
 * nothing else. Once that registry exists, a key that is here and not there, or there and not
 * here, fails review, and each new key goes into both in the same PR pair. Until then, a key goes
 * in here only, and the feature's doc records the missing registry entry as an open item.
 *
 * Each entry is `{ key, type, fallback }`:
 * - `key` is the Datadog flag key, character for character (`rx-[a-z][a-z0-9_-]*`, at most 64).
 *   Every RingbaX key starts with `rx-`, because Portal's flags share the Datadog environments.
 * - `type` is `'boolean'`, `'string'` or `'number'`, and picks the OpenFeature evaluator.
 * - `fallback` is the value every user gets while the provider is not ready, is blocked, or has no
 *   answer for the flag. It is the CURRENT behaviour, so a blocked browser sees today's UI.
 *
 * Read a flag through `this.$featureFlag(FEATURE_FLAG_KEYS.someFlag.key)` or `mapFeatureFlags`
 * (see `src/plugins/featureFlags.js`). Both refuse a key that is not listed here.
 *
 * @type {Readonly<Record<string, { key: string, type: 'boolean' | 'string' | 'number', fallback: boolean | string | number }>>}
 */
export const FEATURE_FLAG_KEYS = Object.freeze({
  // Buyer market context and the campaigns list's inline bid editor. Targeted per user and per
  // account in Datadog, then opened to everyone. UI only: ringba-x has no RingbaXFlags registry
  // yet, and the market-context endpoints are not gated. See docs/MARKET_CONTEXT_FEATURE_FLAG.md.
  marketContext: { key: 'rx-market-context', type: 'boolean', fallback: false }
})

/**
 * The entry for a Datadog key, or `undefined` when the key is not in `FEATURE_FLAG_KEYS`.
 */
export function findFeatureFlagDefinition(key) {
  return Object.values(FEATURE_FLAG_KEYS).find(definition => definition.key === key)
}
